import React, { useEffect, useState } from "react";
import { Trophy, Flame, Clock, Lock, ChevronRight } from "lucide-react";
import api from "../lib/api";

// PremiumAchievementsSection — Profile page section showing the full badge
// dashboard for Premium / Trial / Founder users.
//
// Render contract:
//   - Caller MUST gate this on `effectivePremium === true`. The component
//     does not gate itself — if the caller decides it should show, it shows.
//   - One single fetch on mount (`GET /api/achievements`). No polling, no
//     recompute on every render. Backend returns precomputed badge data.
//
// Rarity tiers are PURELY VISUAL — derived client-side from threshold so we
// don't have to round-trip rarity metadata. Backend remains the source of
// truth for earned/locked state.

const RARITY_STYLES = {
  common:    { ring: "ring-zinc-300 dark:ring-zinc-700",     chip: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",    label: "Common" },
  rare:      { ring: "ring-blue-400/60",                     chip: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",  label: "Rare" },
  epic:      { ring: "ring-purple-400/70",                   chip: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300", label: "Epic" },
  legendary: { ring: "ring-amber-400 ring-offset-2 ring-offset-white dark:ring-offset-zinc-950", chip: "bg-gradient-to-r from-amber-400 to-orange-500 text-white", label: "Legendary" },
};

// Derived from threshold. Highest tiers feel earned.
function rarityOf(badge) {
  const t = badge.threshold;
  if (badge.kind === "study_hours") {
    if (t >= 500) return "legendary";
    if (t >= 100) return "epic";
    if (t >= 30)  return "rare";
    return "common";
  }
  if (badge.kind === "streak") {
    if (t >= 500) return "legendary";
    if (t >= 100) return "epic";
    if (t >= 30)  return "rare";
    return "common";
  }
  if (badge.kind === "chapter") {
    if (t >= 100) return "legendary";
    if (t >= 50)  return "epic";
    if (t >= 25)  return "rare";
    return "common";
  }
  // mock_test
  if (t >= 100) return "legendary";
  if (t >= 50)  return "epic";
  if (t >= 25)  return "rare";
  return "common";
}

const UNIT_BY_KIND = {
  study_hours: "h",
  streak: "d",
  chapter: " ch",
  mock_test: " mocks",
};

function BadgeCard({ badge, earned }) {
  const r = RARITY_STYLES[rarityOf(badge)];
  const unit = UNIT_BY_KIND[badge.kind] || "";
  return (
    <div
      data-testid={`achievement-badge-${badge.id}`}
      data-earned={earned ? "true" : "false"}
      className={`relative flex flex-col items-center rounded-2xl border bg-white p-4 text-center transition-all hover:-translate-y-0.5 dark:bg-zinc-900 ${
        earned
          ? `border-zinc-200 dark:border-zinc-800 ring-2 ${r.ring}`
          : "border-zinc-200 opacity-60 dark:border-zinc-800"
      }`}
    >
      <div className="relative">
        <span className={`text-3xl ${earned ? "" : "grayscale"}`} aria-hidden="true">{badge.emoji}</span>
        {!earned && (
          <span className="absolute -right-2 -bottom-1 grid h-5 w-5 place-items-center rounded-full bg-zinc-200 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            <Lock size={10}/>
          </span>
        )}
      </div>
      <div className="mt-2 font-heading text-xs font-semibold leading-tight">{badge.title}</div>
      <div className="mt-0.5 text-[10px] text-zinc-500">{badge.threshold}{unit}</div>
      <div className={`mt-2 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${r.chip}`}>
        {r.label}
      </div>
    </div>
  );
}

function ProgressCard({ next, label }) {
  if (!next?.badge) {
    return (
      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">{label}</div>
        <div className="mt-2 font-heading text-base font-semibold">All unlocked 🎉</div>
        <div className="mt-1 text-xs text-zinc-500">You&rsquo;ve reached the top of this category.</div>
      </div>
    );
  }
  // Determine kind from badge id prefix or progress payload shape
  const id = next.badge.id || "";
  let kind = "study";
  let current, target, unitLabel;
  if (id.startsWith("study_")) {
    kind = "study"; current = next.current_hours; target = next.target_hours; unitLabel = "Hours";
  } else if (id.startsWith("streak_")) {
    kind = "streak"; current = next.current_days; target = next.target_days; unitLabel = "Days";
  } else if (id.startsWith("chapter_")) {
    kind = "chapter"; current = next.current_chapters; target = next.target_chapters; unitLabel = "Chapters";
  } else {
    kind = "mock"; current = next.current_mocks; target = next.target_mocks; unitLabel = "Mocks";
  }
  const remaining = Math.max(0, Number((target - current).toFixed(kind === "study" ? 1 : 0)));
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid={`next-badge-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">{label}</div>
        <span className="text-xl" aria-hidden="true">{next.badge.emoji}</span>
      </div>
      <div className="mt-1.5 font-heading text-sm font-semibold leading-tight">{next.badge.title}</div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="font-heading text-lg font-bold">{current}<span className="text-sm font-medium text-zinc-500">/{target}</span></span>
        <span className="text-[11px] text-zinc-500">{remaining} {unitLabel} to go</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-800">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

// Pick the most-impressive earned badge: highest threshold, breaking ties by
// streak (commitment > raw hours > chapters > mocks).
function pickTopEarned(...badgeLists) {
  const earned = badgeLists.flat().filter((b) => b.earned);
  if (earned.length === 0) return null;
  const KIND_PRIORITY = { streak: 3, study_hours: 2, chapter: 1, mock_test: 0 };
  return earned.sort((a, b) => {
    if (b.threshold !== a.threshold) return b.threshold - a.threshold;
    return (KIND_PRIORITY[b.kind] || 0) - (KIND_PRIORITY[a.kind] || 0);
  })[0];
}

export default function PremiumAchievementsSection() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/achievements")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        // 402 means premium downgraded between gate & fetch — render nothing
        if (!cancelled) setError(e?.response?.status || "error");
      });
    return () => { cancelled = true; };
  }, []);

  if (error === 402) return null; // premium expired during render
  if (!data) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900" data-testid="achievements-loading">
        <div className="h-4 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800"/>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[0,1,2].map((i) => (<div key={i} className="h-20 animate-pulse rounded-xl bg-zinc-100 dark:bg-zinc-800"/>))}
        </div>
      </div>
    );
  }

  const { stats, progress, study_badges, streak_badges, chapter_badges = [], mock_badges = [] } = data;
  const allBadges = [...study_badges, ...streak_badges, ...chapter_badges, ...mock_badges];
  const top = pickTopEarned(study_badges, streak_badges, chapter_badges, mock_badges);
  const earnedBadges = allBadges.filter((b) => b.earned);
  const lockedBadges = allBadges.filter((b) => !b.earned);
  const totalBadges = allBadges.length;

  return (
    <section className="space-y-5" data-testid="premium-achievements-section">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-600">Premium</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Achievements</h2>
        </div>
      </div>

      {/* Achievement Summary */}
      <div className="grid grid-cols-3 gap-3" data-testid="achievement-summary">
        <SummaryCard icon={Trophy} color="text-amber-500" label="Badges Earned" value={`${stats.earned_count}/${totalBadges}`} testid="summary-badges"/>
        <SummaryCard icon={Flame}  color="text-orange-500" label="Current Streak" value={`${stats.streak_days}d`} testid="summary-streak"/>
        <SummaryCard icon={Clock}  color="text-emerald-500" label="Study Hours" value={`${stats.total_hours}h`} testid="summary-hours"/>
      </div>

      {/* Top Achievement + Next Progress (responsive grid). 4 next-cards for
          the 4 badge categories — collapses to 2 cols on mobile, 4 on lg. */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {/* Top achievement */}
        <div className="lg:col-span-1 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-50 via-white to-orange-50/40 p-4 dark:border-amber-400/20 dark:from-amber-500/10 dark:via-zinc-900 dark:to-orange-500/5" data-testid="top-achievement-card">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">Top Achievement</div>
          {top ? (
            <div className="mt-2 flex items-center gap-3">
              <span className="text-4xl" aria-hidden="true">{top.emoji}</span>
              <div className="min-w-0">
                <div className="font-heading text-sm font-bold leading-tight">{top.title}</div>
                <div className="text-[11px] text-zinc-500">{top.tagline}</div>
              </div>
            </div>
          ) : (
            <div className="mt-2 text-sm text-zinc-500">No badges yet — keep studying, your first one is coming.</div>
          )}
        </div>

        <ProgressCard next={progress.next_study_badge}  label="Next Study Hours Badge" />
        <ProgressCard next={progress.next_streak_badge} label="Next Streak Badge" />
      </div>

      {/* Chapter + Mock-Test next progress (always rendered if data present) */}
      {(progress.next_chapter_badge || progress.next_mock_badge) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {progress.next_chapter_badge && <ProgressCard next={progress.next_chapter_badge} label="Next Chapter Badge" />}
          {progress.next_mock_badge && <ProgressCard next={progress.next_mock_badge} label="Next Mock Test Badge" />}
        </div>
      )}

      {/* Earned + Locked badge grids */}
      {earnedBadges.length > 0 && (
        <div data-testid="earned-badges-block">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400">
            <Trophy size={14}/> Earned <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-300">{earnedBadges.length}</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {earnedBadges.map((b) => (<BadgeCard key={b.id} badge={b} earned/>))}
          </div>
        </div>
      )}

      {lockedBadges.length > 0 && (
        <div data-testid="locked-badges-block">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            <Lock size={14}/> Locked <span className="rounded-full bg-zinc-200 px-1.5 py-0.5 text-[10px] text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">{lockedBadges.length}</span>
            <ChevronRight size={12} className="ml-1 text-zinc-400"/>
            <span className="text-[10px] font-normal normal-case tracking-normal text-zinc-400">Save more sessions to unlock</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {lockedBadges.map((b) => (<BadgeCard key={b.id} badge={b} earned={false}/>))}
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900" data-testid={testid}>
      <div className={`mb-1 ${color}`}><Icon size={16}/></div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-heading text-xl font-bold">{value}</div>
    </div>
  );
}
